/**
 * 구글 — 커스텀 토큰 경로.
 * 이 파일이 아는 것은 "자격증명 얻기" 하나뿐이다.
 * uid 결정·병합·토큰 교환은 게이트웨이와 서버가 한다.
 *
 * ★ 브라우저는 **인증 코드만** 받아서 넘긴다. ID 토큰을 직접 받지 않는다.
 *
 * ID 토큰을 브라우저에서 바로 받는 길은 둘뿐인데 둘 다 막혔다.
 *  - One Tap(`google.accounts.id.prompt()`): 크롬이 FedCM 으로 돌리면서
 *    `isNotDisplayed`/`isSkippedMoment` 콜백을 안 쏜다. 그래서 전에 쓰던 코드는
 *    **프라미스가 영영 안 끝났다** — 버튼이 「확인하는 중…」에서 멈춘 채 에러도 안 났다.
 *  - 구글이 그려 주는 공식 버튼: 모양·높이·글꼴을 우리가 못 정한다.
 *
 * Firebase 의 `signInWithPopup` 도 답이 아니다. 그걸 쓰면 같은 프로젝트에
 * **사용자 레코드가 한 벌 더 생겨서** 서버의 신원 체계(claimIdentity·
 * mergeAnonymousInto)가 오염된다. 격리된 Auth 인스턴스로 띄워도 마찬가지다.
 *
 * 그래서 코드만 받아 서버에 넘기고, 토큰 교환은 서버가 한다.
 * 교환에 클라이언트 시크릿이 필요해서 어차피 브라우저가 할 수 없는 일이다.
 *
 * ★ 팝업이 코드를 부모 창에 돌려주려면 **opener 관계가 살아 있어야** 한다.
 * accounts.google.com 이 COOP 를 걸기 때문에, 우리 쪽이 헤더를 안 주면
 * (unsafe-none) 크롬이 둘을 끊어 버린다. 콘솔에
 *   `Cross-Origin-Opener-Policy policy would block the window.closed call`
 * 이 쌓이고 로그인이 **조용히 안 끝난다.**
 * 그래서 firebase.json 에 `Cross-Origin-Opener-Policy: same-origin-allow-popups`
 * 를 건다. 헤더가 빠지면 이 파일이 아무리 멀쩡해도 로그인이 멈춘다.
 */
export default {
  id: "google",
  label: "Google로 계속하기",
  kind: "exchange",

  async credential(opts = {}) {
    if (opts.idToken) return { idToken: opts.idToken };
    if (opts.code) return { code: opts.code };

    const clientId = opts.clientId;
    if (!clientId) throw new Error("Google 클라이언트 ID 가 필요합니다.");

    const oauth2 = globalThis.google?.accounts?.oauth2;
    if (!oauth2) throw new Error("구글 로그인 스크립트를 불러오지 못했어요. 새로고침하고 다시 해주세요.");

    const code = await new Promise((resolve, reject) => {
      const client = oauth2.initCodeClient({
        client_id: clientId,
        // 신원만 필요하다. 메일·캘린더 같은 건 달라고 하지 않는다 —
        // 동의 화면에 뜨는 목록이 곧 사용자가 느끼는 부담이다.
        scope: "openid email profile",
        ux_mode: "popup",
        callback: (r) => (r?.code ? resolve(r.code) : reject(asError(r))),
        error_callback: (e) => reject(asError(e)),
      });
      client.requestCode();
    });

    return { code };
  },
};

/**
 * 창을 닫은 것과 진짜 실패를 가른다.
 * 닫은 걸 「로그인에 실패했어요」라고 하면 자기가 뭘 잘못한 줄 안다.
 */
function asError(r) {
  const type = r?.type ?? r?.error;
  if (type === "popup_closed" || type === "popup_closed_by_user" || type === "access_denied") {
    return Object.assign(new Error("창이 닫혔어요"), { code: "auth/popup-closed-by-user" });
  }
  if (type === "popup_failed_to_open") {
    return Object.assign(new Error("브라우저가 창을 막았어요"), { code: "auth/popup-blocked" });
  }
  return Object.assign(new Error("구글 로그인을 끝내지 못했어요."), { code: `google/${type ?? "unknown"}` });
}
